package features.teams.data

import kotlin.js.Promise
import core.firebase.FirestoreClient
import features.teams.domain.Team

/**
 * Repository for team database operations
 */
object TeamRepository {
    private val db get() = FirestoreClient.db
    
    fun createTeam(name: String, ownerId: String, avatar: String? = null): Promise<String> {
        console.log("💾 Creating team: $name, has avatar: ${avatar != null}")
        
        // Create team data dynamically
        val teamData = js("{}")
        teamData.name = name
        teamData.ownerId = ownerId
        teamData.memberIds = js("[ownerId]")
        teamData.createdAt = js("new Date()")
        if (avatar != null) {
            teamData.avatar = avatar
            console.log("📸 Avatar included, length: ${avatar.length}")
        }
        
        var teamId = ""
        return db.collection("teams").add(teamData).then { docRef ->
            teamId = docRef.id
            // Add owner as first member
            val memberData = js("""({
                id: ownerId,
                name: window.firebaseAuth.currentUser.displayName || 'Unknown',
                email: window.firebaseAuth.currentUser.email || '',
                role: 'owner',
                joinDate: new Date(),
                quota: 12
            })""")
            
            db.collection("teams").doc(teamId)
                .collection("members").doc(ownerId).set(memberData)
        }.then {
            teamId
        }
    }
    
    fun loadUserTeams(userId: String): Promise<Array<Team>> {
        console.log("📥 Loading teams for user:", userId)
        return db.collection("teams")
            .where("memberIds", "array-contains", userId)
            .get()
            .then { snapshot ->
                console.log("📦 Firestore returned", snapshot.size, "teams")
                val teams = mutableListOf<Team>()
                snapshot.forEach { doc ->
                    val data = doc.data()
                    if (data != null) {
                        val avatarField = data.asDynamic().avatar
                        val avatarValue = if (avatarField != null && avatarField != js("undefined")) {
                            avatarField as String
                        } else {
                            null
                        }
                        
                        val isArchivedField = data.asDynamic().isArchived
                        val isArchivedValue = if (isArchivedField != null && isArchivedField != js("undefined")) {
                            isArchivedField as Boolean
                        } else {
                            false
                        }
                        
                        console.log("Team:", doc.id, "name:", data.asDynamic().name, "has avatar:", avatarValue != null, "archived:", isArchivedValue)
                        if (avatarValue != null) {
                            console.log("  Avatar length:", avatarValue.length, "chars")
                        }
                        
                        // Generate slug from team name (lowercase, replace spaces with hyphens)
                        val teamName = data.asDynamic().name as String
                        val slug = teamName.lowercase().replace(" ", "-").replace(Regex("[^a-z0-9-]"), "")
                        
                        val team = Team(
                            id = doc.id,
                            name = teamName,
                            ownerId = data.asDynamic().ownerId as String,
                            role = if (data.asDynamic().ownerId == userId) "owner" else "member",
                            memberCount = (data.asDynamic().memberIds as Array<*>).size,
                            shiftCount = 0, // Will be loaded separately if needed
                            avatar = avatarValue,
                            isArchived = isArchivedValue,
                            slug = slug
                        )
                        teams.add(team)
                    }
                }
                console.log("✅ Loaded", teams.size, "teams")
                teams.toTypedArray()
            }
    }
    
    fun updateTeamName(teamId: String, name: String): Promise<Unit> {
        console.log("📝 Updating team name:", teamId, "to:", name)
        return db.collection("teams").doc(teamId)
            .update(js("{ name: name }"))
            .then {
                console.log("✅ Team name updated")
            }
    }
    
    fun updateTeamAvatar(teamId: String, avatar: String?): Promise<Unit> {
        console.log("🖼️ Updating team avatar:", teamId, "has avatar:", avatar != null)
        val updateData = if (avatar != null) {
            js("{ avatar: avatar }")
        } else {
            js("{ avatar: null }")
        }
        return db.collection("teams").doc(teamId)
            .update(updateData)
            .then {
                console.log("✅ Team avatar updated")
            }
    }
    
    fun archiveTeam(teamId: String): Promise<Unit> {
        console.log("📦 Archiving team:", teamId)
        return db.collection("teams").doc(teamId)
            .update(js("{ isArchived: true }"))
            .then {
                console.log("✅ Team archived")
            }
    }
    
    fun unarchiveTeam(teamId: String): Promise<Unit> {
        console.log("📂 Unarchiving team:", teamId)
        return db.collection("teams").doc(teamId)
            .update(js("{ isArchived: false }"))
            .then {
                console.log("✅ Team unarchived")
            }
    }
    
    fun checkTeamNameExists(name: String): Promise<Boolean> {
        console.log("🔍 Checking if team exists:", name)
        return db.collection("teams")
            .where("name", "==", name)
            .get()
            .then { snapshot ->
                val exists = snapshot.size > 0
                console.log("🔍 Team '$name' exists:", exists)
                exists
            }
    }
}
